import { useMemo } from "react";
import { StringLike } from "@visx/scale";

import { EmptystateChartColored } from "components/icons/generated";
import Box from "ds/components/Box";
import BarChart from "ds/components/Charts/BarChart";
import { Datum } from "ds/components/Charts/BarChart/Chart";
import { Percentile } from "ds/components/Charts/types";
import EmptyState from "ds/components/EmptyState";

import styles from "./styles.module.css";
import { ChartColor } from "./types";

type UsageChartViewProps = {
  data: Datum[];
  items: string[];
  colorMapper: (item: string) => ChartColor;
  formatXAxisLabel: (value: StringLike) => string;
  xKey?: string;
  tooltipHeader?: string;
  legendTitle?: string;
  leftAxisLabel?: string;
  leftNumTicks?: number;
  showItemsInTooltip?: boolean;
  percentiles?: Percentile[];
};

const UsageChartView = ({
  data,
  items,
  colorMapper,
  formatXAxisLabel,
  xKey = "timestamp",
  tooltipHeader,
  legendTitle,
  leftAxisLabel,
  leftNumTicks = 6,
  showItemsInTooltip = true,
  percentiles,
}: UsageChartViewProps) => {
  const [colors, inactiveColors] = useMemo(
    () => [
      items.map((item) => colorMapper(item).primary),
      items.map((item) => colorMapper(item).inactive),
    ],
    [items, colorMapper]
  );

  if (!data.length) {
    return (
      <EmptyState
        title="There's no data to show in the usage chart yet"
        icon={EmptystateChartColored}
      />
    );
  }

  return (
    <Box padding="large" className={styles.chartWrapper}>
      <BarChart
        data={data}
        items={items}
        colors={colors}
        inactiveColors={inactiveColors}
        xKey={xKey}
        formatXAxisLabel={formatXAxisLabel}
        tooltipHeader={tooltipHeader}
        legendTitle={legendTitle}
        legendAlign="start"
        leftNumTicks={leftNumTicks}
        showItemsInTooltip={showItemsInTooltip}
        percentiles={percentiles}
        leftAxisLabel={leftAxisLabel}
      />
    </Box>
  );
};

UsageChartView.displayName = "UsageChart.View";

export default UsageChartView;
